/**
 * `/d/:department/u/:slug` — a utility page (`docs/UTILITIES.md` §6).
 *
 * The title comes from the department listing, which is filtered server-side:
 * a slug the caller cannot reach is not in it, and the page says so exactly as
 * a report would, without confirming that the utility exists. The page's own
 * endpoints re-check access on every call; this is presentation only.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router'

import { api } from '../lib/api'
import { useMe } from '../lib/session'
import { UTILITIES } from '../utilities'
import { ErrorState, Skeleton } from '../components/States'

export function Utility() {
  const { department = '', slug = '' } = useParams()
  const me = useMe()
  const { data, error, isPending } = useQuery({
    queryKey: ['department-reports', department],
    queryFn: () => api.departmentReports(department),
  })

  if (isPending) return <Skeleton rows={3} />
  if (error !== null) return <ErrorState error={error} />

  const entry = data.find((item) => item.slug === slug && item.kind === 'utility')
  if (entry === undefined) {
    return (
      <p role="alert" className="text-[length:var(--text-sm)] text-[var(--color-negative)]">
        This utility is not available to you.
      </p>
    )
  }

  const departmentName = me?.departments.find((item) => item.key === department)?.name ?? department
  const Page = UTILITIES[slug]

  return (
    <div>
      <nav className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
        <Link to={`/d/${department}`} className="hover:text-[var(--color-ink)]">
          {departmentName}
        </Link>
        <span aria-hidden> / </span>
        <span>{entry.title}</span>
      </nav>
      <h1 className="mt-1 flex items-center gap-2 text-[length:var(--text-lg)] font-semibold tracking-tight">
        <span aria-hidden className="h-5 w-1 rounded-full bg-[var(--accent)]" />
        {entry.title}
      </h1>
      <p className="mt-1 max-w-3xl text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
        {entry.summary}
      </p>
      <div className="mt-6">
        {Page === undefined ? (
          <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">
            This utility is registered but its page has not shipped yet.
          </p>
        ) : (
          <Page slug={slug} department={department} />
        )}
      </div>
    </div>
  )
}
